import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LibraryBooks as LibraryBooksIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { categoryBarColor } from './okrColors';
import {
  getOkrTemplateLibrary,
  setOkrTemplateLibrary,
  templateCategoryCodeForCategory,
  catalogKeyResultCodeString
} from './okrKeyResultNameTemplates';

function matchCatalogRowForSeedTemplate(template, catalogKrs) {
  if (!template || !Array.isArray(catalogKrs)) return null;
  const tid = String(template.id);
  const bySource = catalogKrs.find(
    (kr) => String(kr.sourceTemplateId || kr.source_template_id || '') === tid
  );
  if (bySource) return bySource;
  const wantName = String(template.name || '').trim();
  return catalogKrs.find((kr) => String((kr.name || '').trim()) === wantName) || null;
}

export default function OkrCategories() {
  const { hasPermission, hasModuleAccess } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    theme_color: '#6366f1',
    sort_order: 0,
    is_active: true,
    weight_percent: 0,
  });
  const [seedViewOpen, setSeedViewOpen] = useState(false);
  const [seedViewRow, setSeedViewRow] = useState(null);
  const [seedViewKrs, setSeedViewKrs] = useState([]);
  const [seedViewLoading, setSeedViewLoading] = useState(false);
  const [templateLibraryVersion, setTemplateLibraryVersion] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/okr/categories');
      setRows(r.data?.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get('/okr/key-result-name-templates')
      .then((r) => {
        setOkrTemplateLibrary(r.data?.data?.templates || []);
        setTemplateLibraryVersion((v) => v + 1);
      })
      .catch(() => {
        setTemplateLibraryVersion((v) => v + 1);
      });
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      theme_color: '#6366f1',
      sort_order: rows.length,
      is_active: true,
      category_code: '',
      weight_percent: 0,
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      description: row.description || '',
      theme_color: row.theme_color || '#6366f1',
      sort_order: Number(row.sort_order || 0),
      is_active: row.is_active !== false,
      category_code: row.category_code || '',
      weight_percent: Number(row.weight_percent ?? 0),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        description: form.description,
        theme_color: form.theme_color,
        sort_order: form.sort_order,
        is_active: form.is_active,
        weight_percent: Math.min(100, Math.max(0, Number(form.weight_percent) || 0)),
      };
      if (editing) {
        const r = await api.put(`/okr/categories/${editing.id}`, payload);
        toast.success('Key Result Category updated');
        const seeded = r.data?.data?.system_templates_seeded;
        if (seeded > 0) {
          toast.info(
            `Created ${seeded} system catalog key result(s) with KR ids. Add job-role targets from OKRs → KR+Cat(%)+Target(Nos).`
          );
        }
        if (r.data?.data?.system_templates_seed_error) {
          toast.warn(`Template seed note: ${r.data.data.system_templates_seed_error}`);
        }
      } else {
        const r = await api.post('/okr/categories', payload);
        toast.success('Key Result Category created');
        const seeded = r.data?.data?.system_templates_seeded;
        if (seeded > 0) {
          toast.info(
            `Created ${seeded} system catalog key result(s) with KR ids (name matches a template library family). Add targets in the matrix when ready.`
          );
        }
        if (r.data?.data?.system_templates_seed_error) {
          toast.warn(`Template seed note: ${r.data.data.system_templates_seed_error}`);
        }
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const openSeedView = useCallback(async (row) => {
    setSeedViewRow(row);
    setSeedViewOpen(true);
    setSeedViewKrs([]);
    setSeedViewLoading(true);
    try {
      const r = await api.get(`/okr/catalog-key-results?category_id=${encodeURIComponent(row.id)}`);
      setSeedViewKrs(r.data?.data?.key_results || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load catalog lines for this category.');
      setSeedViewKrs([]);
    } finally {
      setSeedViewLoading(false);
    }
  }, []);

  const closeSeedView = useCallback(() => {
    setSeedViewOpen(false);
    setSeedViewRow(null);
    setSeedViewKrs([]);
    setSeedViewLoading(false);
  }, []);

  const seedViewLibraryCode = useMemo(
    () => (seedViewRow ? templateCategoryCodeForCategory(seedViewRow) : null),
    [seedViewRow]
  );

  const seedViewTemplates = useMemo(() => {
    if (!seedViewLibraryCode) return [];
    const want = String(seedViewLibraryCode).toUpperCase();
    return getOkrTemplateLibrary().filter(
      (t) => String(t.categoryCode || '').toUpperCase() === want
    );
  }, [seedViewLibraryCode, templateLibraryVersion]);

  const seedViewExtraCatalogCount = useMemo(() => {
    if (!seedViewTemplates.length || !seedViewKrs.length) return seedViewKrs.length;
    const matched = new Set();
    for (const t of seedViewTemplates) {
      const kr = matchCatalogRowForSeedTemplate(t, seedViewKrs);
      if (kr?.id) matched.add(String(kr.id));
    }
    return seedViewKrs.filter((kr) => !matched.has(String(kr.id))).length;
  }, [seedViewTemplates, seedViewKrs]);

  const seedSystemTemplates = async (row) => {
    try {
      const r = await api.post(`/okr/categories/${row.id}/seed-system-templates`);
      const { created, skipped, libraryCode } = r.data?.data || {};
      if (libraryCode == null) {
        toast.info('No template library family matched this category name or Category ID — nothing to seed.');
      } else if (created > 0) {
        toast.success(`Added ${created} system catalog line(s) (${libraryCode} family). Skipped ${skipped || 0} already present or inactive.`);
      } else {
        toast.info(`Nothing new to add (${libraryCode} family). Skipped ${skipped || 0} already present or inactive.`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Seed failed');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete Key Results category “${row.name}”?`)) return;
    try {
      await api.delete(`/okr/categories/${row.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Key Result Categories
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Name, color, <strong>portfolio weight %</strong> (how much this category counts in the overall OKR mix — active
            categories should total 100%), and enable or disable. When the name (or Category ID) matches a built-in
            template family, new categories automatically get <strong>system catalog key results</strong> with KR ids; use{' '}
            <strong>Seed KRs</strong> on an existing category to run the same step. Job-role targets are still added in{' '}
            <strong>OKRs → KR+Cat(%)+Target(Nos)</strong>.
          </Typography>
        </Box>
        {hasPermission('okr_add') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            New Key Result Category
          </Button>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Grid container spacing={2}>
          {rows.map((row, i) => {
            const c = row.theme_color || categoryBarColor(i);
            return (
              <Grid item xs={12} md={6} key={row.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    borderLeft: `4px solid ${c}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 3 }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {row.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                          {row.category_code && (
                            <Chip
                              size="small"
                              label={row.category_code}
                              sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}
                              variant="outlined"
                            />
                          )}
                          {Number(row.weight_percent) > 0 && (
                            <Chip size="small" label={`Portfolio ${Number(row.weight_percent).toFixed(1)}%`} variant="outlined" />
                          )}
                        </Box>
                        {row.description ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          >
                            {row.description}
                          </Typography>
                        ) : null}
                      </Box>
                      <Chip
                        size="small"
                        label={row.is_active !== false ? 'Enabled' : 'Disabled'}
                        color={row.is_active !== false ? 'success' : 'default'}
                        variant={row.is_active !== false ? 'filled' : 'outlined'}
                      />
                    </Box>
                    {row.is_system && <Chip size="small" label="Seed" sx={{ mt: 1 }} variant="outlined" />}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {hasModuleAccess('okr') && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => openSeedView(row)}
                          title="Preview built-in template library rows for this category and what is already in the catalog"
                        >
                          View Seed KRs
                        </Button>
                      )}
                      {hasPermission('okr_modify') && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LibraryBooksIcon />}
                          onClick={() => seedSystemTemplates(row)}
                          title="Create missing system catalog lines from the name-template library for this category (idempotent)"
                        >
                          Seed KRs
                        </Button>
                      )}
                      {hasPermission('okr_modify') && (
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                      )}
                      {hasPermission('okr_delete') && !row.is_system && (
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(row)}>
                          Delete
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={seedViewOpen} onClose={closeSeedView} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Seed / template library — {seedViewRow?.name || 'Category'}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              These rows are <strong>not</strong> loaded from a <code>.dat</code> file. The names and descriptions come
              from the built-in library in the app source:{' '}
              <Typography component="span" variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                erp4c-base-frontend/src/modules/okr/okrKeyResultNameTemplates.js
              </Typography>{' '}
              (UI and this list). The server uses the same definitions in{' '}
              <Typography component="span" variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                erp4c-base-backend/src/config/okrKeyResultNameTemplatesSeed.js
              </Typography>{' '}
              when it creates <strong>SYST</strong> catalog lines. After you seed, each line is stored in{' '}
              <strong>PostgreSQL</strong> (<code>okr_catalog_key_results</code>), including <code>kr_code</code> and{' '}
              <code>source_template_id</code>.
            </Typography>
          </Alert>
          {seedViewLoading ? (
            <LinearProgress />
          ) : seedViewLibraryCode == null ? (
            <Typography variant="body2" color="text.secondary">
              This category name and Category ID do not map to a known template family (CNDP, CLBD, BPRC, SOBR, LRNG).
              There is nothing to show for &quot;View Seed KRs&quot;; use a matching name or a known four-letter Category
              ID, or keep using custom catalog lines only.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Template family <Chip size="small" label={seedViewLibraryCode} sx={{ fontWeight: 700 }} /> —{' '}
                {seedViewTemplates.length} built-in name template(s). Catalog lines below are restricted to this
                category (current DB).
              </Typography>
              {seedViewExtraCatalogCount > 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  This category also has {seedViewExtraCatalogCount} other catalog line(s) (e.g. custom) not listed in
                  the template table.
                </Typography>
              ) : null}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Template id</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        In catalog
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>KR id (stored)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {seedViewTemplates.map((t) => {
                      const kr = matchCatalogRowForSeedTemplate(t, seedViewKrs);
                      const code = catalogKeyResultCodeString(kr);
                      return (
                        <TableRow key={t.id} hover>
                          <TableCell sx={{ fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>{t.id}</TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>{t.name}</TableCell>
                          <TableCell align="center">
                            {kr ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" variant="outlined" label="No" />}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>{code || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSeedView}>Close</Button>
          {hasPermission('okr_modify') && seedViewRow && seedViewLibraryCode != null ? (
            <Button
              variant="contained"
              startIcon={<LibraryBooksIcon />}
              onClick={async () => {
                await seedSystemTemplates(seedViewRow);
                closeSeedView();
              }}
            >
              Seed KRs now
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Key Result Category' : 'New Key Result Category'}</DialogTitle>
        <DialogContent>
          {editing && form.category_code && (
            <TextField
              fullWidth
              label="Category ID (4 characters)"
              margin="normal"
              value={form.category_code}
              InputProps={{ readOnly: true }}
              helperText="Used as the prefix for catalog Key Result IDs (e.g. XXXX-0001-0000-0001)."
            />
          )}
          {!editing && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              A unique 4-character Category ID is assigned automatically when you save. If the name matches a template
              library family (e.g. Social Media and Brand), the server also creates system catalog lines with KR ids
              (no job-role targets until you add them in the matrix).
            </Typography>
          )}
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            minRows={2}
            placeholder="Optional notes for this category"
          />
          <TextField
            fullWidth
            type="number"
            label="Portfolio weight % (OKR mix)"
            margin="normal"
            value={form.weight_percent}
            onChange={(e) => setForm({ ...form, weight_percent: e.target.value === '' ? '' : Number(e.target.value) })}
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            helperText="Share of the overall OKR portfolio for this category (Employee Map and scores use this). Active categories should add up to 100%."
          />
          <TextField
            fullWidth
            label="Color"
            margin="normal"
            value={form.theme_color}
            onChange={(e) => setForm({ ...form, theme_color: e.target.value })}
            placeholder="#6366f1"
            helperText="Hex color for labels and matrix highlights"
          />
          <TextField
            fullWidth
            type="number"
            label="Sort order"
            margin="normal"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                color="primary"
              />
            }
            label="Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
